/**
 * AuthzForbiddenBanner — polished error alert for permission denials.
 *
 * Two sources feed it:
 *   1. Every apiFetch 403 dispatches `tile:authz-forbidden`
 *      (see utils/api/client.ts).
 *   2. A once-installed fetch interceptor mirrors the same event for
 *      raw window.fetch() calls that bypass apiFetch.
 *
 * Coalesces bursts (~1.5s) so parallel calls denied by the same missing
 * scope render one alert, not five. Auto-dismisses after 6s.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { isEmbedSession } from '@/app/bootstrap/embed-session/embed-session';

interface ForbiddenDetail {
  url?: string;
  action?: string;
  resource?: string;
  reason?: string;
  message?: string;
}

let fetchIntercepted = false;
function installFetchInterceptor(): void {
  if (fetchIntercepted || typeof window === 'undefined') return;
  fetchIntercepted = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const res = await orig(...args);
    if (res.status === 403) {
      let detail: ForbiddenDetail = {
        url: (args[0] as any)?.url ?? String(args[0]),
      };
      try {
        const clone = res.clone();
        const body: any = await clone.json();
        detail = {
          ...detail,
          action: body?.action,
          resource: body?.resource,
          reason: body?.reason,
          message: body?.error,
        };
      } catch {
        /* body isn't JSON, that's fine */
      }
      window.dispatchEvent(new CustomEvent('tile:authz-forbidden', { detail }));
    }
    return res;
  };
}

const STYLE_ID = '__tile_authz_banner_styles__';
const STYLES = `
@keyframes tile-authz-slide-in {
  from { transform: translate(-50%, -12px); opacity: 0; }
  to   { transform: translate(-50%, 0);     opacity: 1; }
}
@keyframes tile-authz-slide-out {
  from { transform: translate(-50%, 0);     opacity: 1; }
  to   { transform: translate(-50%, -12px); opacity: 0; }
}
.tile-authz-alert {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  min-width: 380px;
  max-width: 520px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  background: #ffffff;
  color: #1a1a1a;
  border-radius: 12px;
  border: 1px solid rgba(220, 38, 38, 0.22);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.04),
    0 12px 32px rgba(220, 38, 38, 0.14),
    0 4px 12px rgba(0, 0, 0, 0.08);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  animation: tile-authz-slide-in 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.tile-authz-alert.leaving {
  animation: tile-authz-slide-out 200ms cubic-bezier(0.4, 0, 1, 1) forwards;
}
.tile-authz-alert__icon {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: rgba(220, 38, 38, 0.09);
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tile-authz-alert__body {
  flex: 1;
  min-width: 0;
}
.tile-authz-alert__title {
  margin: 0 0 4px;
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #1a1a1a;
}
.tile-authz-alert__msg {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #484848;
}
.tile-authz-alert__meta {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(0, 0, 0, 0.08);
  font-size: 11.5px;
  color: #777;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
}
.tile-authz-alert__meta code {
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 11px;
  background: #f3f3f3;
  padding: 1px 6px;
  border-radius: 4px;
  color: #1a1a1a;
}
.tile-authz-alert__close {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border: 0;
  background: transparent;
  color: #777;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease, color 120ms ease;
}
.tile-authz-alert__close:hover {
  background: #f3f3f3;
  color: #1a1a1a;
}
`;

function ensureStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}

function humaniseAction(action?: string): string | null {
  if (!action) return null;
  // "tile:apps.write" → "apps.write" → "Modify apps"
  const bare = action.replace(/^tile:/, '');
  const [resource, verb] = bare.split('.');
  if (!verb) return bare;
  const verbLabel =
    { read: 'view', write: 'modify', delete: 'delete', admin: 'administer' }[verb] ?? verb;
  return `${verbLabel} ${resource}`;
}

export default function AuthzForbiddenBanner() {
  const [current, setCurrent] = useState<ForbiddenDetail | null>(null);
  const [leaving, setLeaving] = useState(false);
  const lastShownAt = useRef(0);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = () => {
    setLeaving(true);
    setTimeout(() => {
      setCurrent(null);
      setLeaving(false);
    }, 200);
  };

  useEffect(() => {
    ensureStyles();
    installFetchInterceptor();
    const onDenied = (e: Event) => {
      const detail = (e as CustomEvent<ForbiddenDetail>).detail || {};
      const now = Date.now();
      if (now - lastShownAt.current < 1500) return;
      lastShownAt.current = now;
      setLeaving(false);
      setCurrent(detail);
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
      autoCloseTimer.current = setTimeout(() => {
        dismiss();
      }, 6000);
    };
    window.addEventListener('tile:authz-forbidden', onDenied);
    return () => {
      window.removeEventListener('tile:authz-forbidden', onDenied);
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    };
  }, []);

  if (!current || typeof document === 'undefined') return null;

  const humanAction = humaniseAction(current.action);

  return ReactDOM.createPortal(
    <div className={`tile-authz-alert${leaving ? ' leaving' : ''}`} role="alert">
      <div className="tile-authz-alert__icon" aria-hidden>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 5h2v6h-2V7zm0 8h2v2h-2v-2z"
            fill="currentColor"
          />
        </svg>
      </div>
      <div className="tile-authz-alert__body">
        <h4 className="tile-authz-alert__title">Access denied</h4>
        {/* An embedded session is scoped to one app by its host, so the
            org-administrator advice below would be wrong (the viewer may
            not even know this org exists). */}
        {isEmbedSession() ? (
          <p className="tile-authz-alert__msg">
            This action isn&rsquo;t available in the embedded editor
            {humanAction ? <> (it needs permission to {humanAction})</> : null}.
          </p>
        ) : (
          <p className="tile-authz-alert__msg">
            You don&rsquo;t have permission to
            {humanAction ? <> {humanAction}</> : <> perform this action</>}. Contact an
            administrator of this organization to request access.
          </p>
        )}
        {current.action || current.reason ? (
          <div className="tile-authz-alert__meta">
            {current.action ? (
              <span>
                Scope <code>{current.action}</code>
              </span>
            ) : null}
            {current.reason ? <span>Reason: {current.reason}</span> : null}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="tile-authz-alert__close"
        onClick={dismiss}
        aria-label="Dismiss"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M2 2l8 8M10 2l-8 8"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>,
    document.body,
  );
}
